import { Injectable } from "@angular/core";
import { PackageService } from "./package.service";
import { AccountService } from "../account/account.service";
import { AccountInvoiceService } from "../accountInvoice/account-invoice.service";
import { Observable, combineLatest, filter, map, of, switchMap } from "rxjs";
import { DisplayPackageName, PAY_AS_YOU_GO_PLAN, PER_SEAT_UNLIMITED_PLAN } from "./package";

@Injectable({ providedIn: "root" })
export class GetPrimaryPackageNameService {
  constructor(
    private packageService: PackageService,
    private accountService: AccountService,
    private accountInvoiceService: AccountInvoiceService
  ) {}

  /** returns observable of display name of primary package based of design's categorization of our plan packages */
  getDisplayName(): Observable<DisplayPackageName> {
    return this.packageService.getAssignedPrimaryPackages().pipe(
      switchMap(packages => {
        if (packages.length === 0) {
          return combineLatest({
            currentAccount: this.accountService.state.pipe(
              filter(state => !state.loading),
              map(state => Object.values(state.state)[0])
            ),
            accountInvoice: this.accountInvoiceService.accountState.pipe(map(account => account[0]))
          }).pipe(
            map(({ currentAccount, accountInvoice }) => {
              const isPaying = !!currentAccount.creditCard || currentAccount.invoicedPrepaid;
              const latestResourcesSubTotal =
                accountInvoice?.itemGroups?.find(item => item.type === "Resource")?.subTotal || "0";
              if (isPaying && parseFloat(latestResourcesSubTotal) > 0) {
                return DisplayPackageName.NO_PLAN_A_LA_CARTE;
              }
              return DisplayPackageName.FREE_PLAN;
            })
          );
        } else {
          // We are going to consider any accounts with multiple plans as having a custom plan
          const packageNames = packages.map(plan => plan.displayName);
          if (
            packageNames.length === 1 &&
            packageNames.find(name => name === PER_SEAT_UNLIMITED_PLAN)
          ) {
            return of(DisplayPackageName.UNLIMITED_PLAN);
          } else if (
            packageNames.length === 1 &&
            packageNames.find(name => name === PAY_AS_YOU_GO_PLAN)
          ) {
            return of(DisplayPackageName.BASIC_PLAN);
          } else {
            return of(DisplayPackageName.CUSTOM_PLAN);
          }
        }
      })
    );
  }

  isNotFreePlan$(): Observable<boolean> {
    return this.getDisplayName().pipe(
      map(
        name =>
          name !== DisplayPackageName.NO_PLAN_A_LA_CARTE && name !== DisplayPackageName.FREE_PLAN
      )
    );
  }
}
